import { DASHBOARD_HUB_LINKS } from '@/@dashboards/hubs';
import { FeasibilityDaniRoutes, Routes } from '@/app/routes';

/**
 * Design source of record: `MFour Studio JTBD v15.dc.html` (see PRD
 * STUDIO-HOME-001, Confluence page 3981967386). These four stage colors are
 * the mockup's own `--stage-*` tokens, AA-verified with white text. Kept
 * local to this module rather than added to the global Tailwind theme —
 * that global design-token system is PRD Phase 1, not part of this build.
 */
export const STAGE_COLORS = {
    create: '#1D4ED8',
    analyze: '#5B21B6',
    report: '#C2410C',
    explore: '#0B7A52',
} as const;

/**
 * Slide Creator's identity colour, named because three surfaces draw it: this tile,
 * the left-hand nav, and the page's own header. A lookup by tile name would resolve
 * to a silent fallback the day someone renames the tile.
 *
 * Steel blue because the Report row reads left to right as Quick Facts,
 * Slide Creator, Board Reports, and the other two are a rust and a purple. Taken from
 * the palette already in use here rather than invented.
 */
export const SLIDE_CREATOR_COLOR = '#33738D';

/** Dark panel fill used by every stage's expand panel in the mockup. */
export const PANEL_DARK_BG = '#192138';

export interface StageTileAction {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    /** Sends this text to Ask DANI as a seed prompt. Mutually exclusive
     * with `href` — a given action does one or the other. */
    prompt?: string;
    /** Navigates directly to this route instead of seeding a DANI prompt —
     * used by tiles whose hover list picks a destination page rather than
     * asking DANI a question (e.g. the Dashboards tile's Sigma links). */
    href?: string;
    /** Where the tile itself goes while this choice is available — same
     * contract as `NavChildFlyoutItem.primary`. */
    primary?: boolean;
    /** Only rendered when this feature flag is on — same contract as
     * `NavChildFlyoutItem.requiresFlag` so Home and the left-hand nav
     * cannot drift on a gated dashboard. */
    requiresFlag?: string;
    /** Only rendered for internal viewers, or external viewers whose own
     * org name contains this string — same contract as
     * `NavChildFlyoutItem.requiresOrgNameMatch`, for a client-specific
     * dashboard with no flag or license of its own to gate on. */
    requiresOrgNameMatch?: string;
}

export interface HomeTile {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    /** Declared destination — NOT a permission claim. Whether this viewer may
     * reach it is decided by the matching left-nav row via `tile-access.ts`,
     * so a tile cannot enable itself. Mutually exclusive with `seedPrompt`. */
    href?: string;
    /** Sends this text to Ask DANI (`/dani?seed=...`) instead of navigating directly. */
    seedPrompt?: string;
    /**
     * Overrides the default shade-of-the-section-color tile background
     * (PRD Decision 8) with this exact color. Used where each tile needs
     * its own distinct identity rather than reading as one family — e.g.
     * Analyze's tiles, by explicit request.
     */
    color?: string;
    /** Suppresses the "Coming soon" caption on a disabled tile — for
     * placeholders that should just look quietly inert rather than call
     * attention to a future rollout. */
    hideComingSoonLabel?: boolean;
    /**
     * Forces this tile into the disabled treatment regardless of any
     * viewer's entitlements — for a `TILES_WITHOUT_NAV_COUNTERPART` tile
     * that is held back for review, not gated by a real flag or license.
     * Deliberately distinct from (and not confused with) `comingSoon`,
     * which `home-nav-parity.test.ts` forbids any tile from declaring —
     * that guard exists to stop a *governed* tile from self-disabling to
     * hide access-control drift, which this cannot do since it only takes
     * effect for tiles already exempt from nav governance.
     */
    isTemporarilyInactive?: boolean;
    /** A hover-tooltip list of clickable sub-items instead of a single
     * plain-text `tooltip` — a set of direct-navigation links (e.g. the
     * Dashboards tile's Sigma dashboards). Connect's tiles used to carry
     * their per-channel commands here too; removed for now (the flyout
     * overlapped page content below the panel) in favor of a plain
     * `seedPrompt`. */
    actions?: StageTileAction[];
    /** Clicking this tile drills into `actions` as a grid of icon tiles —
     * the same back-button pattern Connect's Profiles/Behavior/Exposure
     * categories use — instead of the hover dropdown `actions` normally
     * renders. The items shown are still `actions`, gated the same way
     * (flag/org-name fan-out filtering); this only changes how they're
     * presented once the viewer clicks in. Mutually exclusive with
     * `href`/`seedPrompt` in practice — a tile that opens its own actions
     * panel has no destination of its own, only its actions do. */
    opensActionsPanel?: boolean;
    /** Plain-text hover tooltip shown only to internal (@mfour.com) users —
     * roadmap/status context for tiles whose real-world state (internal
     * build, external legacy, etc.) isn't obvious from the tile alone.
     * Mutually exclusive with `seedPrompt`/`actions` in practice (no tile
     * uses both), but takes priority over `seedPrompt` when both are set
     * and the viewer is internal. */
    internalTooltip?: string;
    /** Adds a white border to the icon chip — for a stage whose tiles are
     * all one color family (e.g. Field's blue variants) and need the
     * border to keep each icon legible against the panel background. */
    hasIconBorder?: boolean;
}

/**
 * The four vTracker dashboards (admin Dashboards report, `is_vtracker`
 * records), shared by the Home screen's Dashboards tile and the left nav's
 * Dashboards flyout so the two cannot list a different set. Each is gated by
 * `requiresOrgNameMatch` to the one client it belongs to — internal viewers
 * see all four regardless.
 *
 * The match string is a name-contains heuristic, not a verified Salesforce
 * Account ID: at the time this was added, none of these orgs existed in
 * this environment's Clients admin list to confirm an exact account id
 * against. Replace with an id-based check if one becomes verifiable.
 *
 * A fifth entry, "Altira vTracker+", was removed: no independent source
 * (an unrelated Sigma-inspection doc on another branch, the admin Dashboards
 * report this list is sourced from) ever names a real "Altira" org, only
 * "Altria" — this was a one-letter mistyped duplicate, not a second client.
 *
 * Each `href` ends in the dashboard's Code and each `name` is its Name, both
 * exactly as the production admin Dashboards report lists them (checked
 * 2026-10-12, BT-2103). Cargurus's code is not `cargurus_vtracker`, which
 * 404s.
 *
 * Deliberately untyped as `StageTileAction[]` — every entry's `href` is a
 * concrete string, so leaving it inferred keeps the array assignable to
 * both `StageTileAction[]` (Home's optional `href?`) and
 * `NavChildFlyoutItem[]` (the nav's required `href`) without a cast.
 */
export const VTRACKER_DASHBOARDS = [
    {
        name: 'Burlington vTracker+',
        icon: 'radar',
        href: '/custom-dashboards/burlington_vtracker',
        requiresOrgNameMatch: 'burlington',
    },
    {
        name: 'Cargurus vTracker+',
        icon: 'radar',
        href: '/custom-dashboards/cargurus_app_and_web_visitors_vtracker',
        requiresOrgNameMatch: 'cargurus',
    },
    {
        name: 'Altria vTracker+',
        icon: 'radar',
        href: '/custom-dashboards/altria_vtracker_plus',
        requiresOrgNameMatch: 'altria',
    },
    {
        name: 'NACS',
        icon: 'radar',
        href: '/custom-dashboards/nacs_vtracker_plus',
        requiresOrgNameMatch: 'nacs',
    },
];

/**
 * The two feasibility tools, shared by the Home screen's Feasibility tile and
 * the left nav's Feasibility flyout so the two cannot offer a different pair.
 * DANI Feasibility needs `feasibility-dani` (its page's own layout checks the
 * same flag). With the flag off only the legacy row survives, and a lone
 * choice is no choice: the row and the tile stay a plain link to the legacy
 * tool (`choiceOrNothing` in `@nav/access/resolve-access`). With the flag on,
 * clicking the row or the tile itself opens DANI Feasibility (`primary`); the
 * legacy tool stays in the list.
 *
 * Left untyped for the same reason as `VTRACKER_DASHBOARDS`.
 */
export const FEASIBILITY_CHOICES = [
    { name: 'Legacy Feasibility', icon: 'target', href: Routes.FEASIBILITY },
    {
        name: 'DANI Feasibility',
        icon: 'sparkles',
        href: FeasibilityDaniRoutes.FEASIBILITY_INTERNAL,
        requiresFlag: 'feasibility-dani',
        primary: true,
    },
];

/**
 * A drill-down group within a stage's panel (Connect's Profiles / Behavior /
 * Exposure) — clicking it swaps the panel's tile row for `tiles` and the
 * panel's corner icon for this group's own `icon`, with the corner icon
 * doubling as the back button to return to the group picker. Distinct from
 * `HomeTile`: a group has no destination of its own and is never governed by
 * `tile-access.ts` — only the tiles inside it are.
 */
export interface HomeCategoryGroup {
    name: string;
    /** Iconify slug without the `lucide:` prefix. */
    icon: string;
    color: string;
    /** Adds a white border to the group's own icon chip — see
     * `HomeTile.hasIconBorder`. */
    hasIconBorder?: boolean;
    tiles: HomeTile[];
}

export interface HomeStage {
    key: string;
    /** Display label — rendered uppercase; stored in normal case here. */
    stage: string;
    icon: string;
    color: string;
    tagline: string;
    /** Flat tile row. Empty (and unused) for a stage that sets `categories`
     * instead. */
    tiles: HomeTile[];
    /** When set, the panel shows this stage's groups first (Connect's
     * Profiles / Behavior / Exposure) rather than `tiles` directly — see
     * `HomeCategoryGroup`. */
    categories?: HomeCategoryGroup[];
}

export const HOME_STAGES: HomeStage[] = [
    {
        key: 'create',
        stage: 'Field',
        icon: 'pencil-ruler',
        color: STAGE_COLORS.create,
        tagline: 'Launch studies & track fieldwork.',
        tiles: [
            {
                name: 'Feasibility',
                icon: 'target',
                href: Routes.FEASIBILITY,
                actions: FEASIBILITY_CHOICES,
                color: '#2C56DD',
                hasIconBorder: true,
                internalTooltip: 'Roadmap v2.1 (Internal Only)',
            },
            {
                name: 'Builder',
                icon: 'pencil-ruler',
                href: Routes.BUILDER,
                color: '#5879E4',
                hasIconBorder: true,
                internalTooltip: 'Roadmap v2.2 (External Legacy DIY)',
            },
            {
                name: 'Status',
                icon: 'clipboard-list',
                href: Routes.MY_SURVEYS,
                color: '#728DE0',
                hasIconBorder: true,
            },
        ],
    },
    {
        key: 'analyze',
        stage: 'Analyze',
        icon: 'bar-chart-3',
        color: STAGE_COLORS.analyze,
        tagline: 'Explore results & find insights.',
        tiles: [
            {
                name: 'Videos',
                icon: 'clapperboard',
                href: Routes.MY_SURVEYS_WITH_MULTIMEDIA,
                color: '#6842B8',
                hasIconBorder: true,
            },
            {
                name: 'Crosstabs',
                icon: 'table-2',
                href: Routes.CROSSTABS,
                color: '#8363C4',
                hasIconBorder: true,
            },
            {
                name: 'Ask DANI',
                icon: 'sparkles',
                href: Routes.STUDIO_AI,
                color: '#9D83D0',
                hasIconBorder: true,
            },
        ],
    },
    {
        key: 'explore',
        stage: 'Connect',
        icon: 'database',
        color: STAGE_COLORS.explore,
        tagline: 'Unify data across channels.',
        // Superseded by `categories` below — the twelve channels now drill
        // down through Profiles / Behavior / Exposure instead of rendering
        // flatly. Kept as `[]` rather than removed: every other stage still
        // relies on `tiles` being present.
        tiles: [],
        categories: [
            {
                name: 'Profiles',
                icon: 'id-card',
                color: '#2E6649',
                hasIconBorder: true,
                tiles: [
                    {
                        name: 'Demographics',
                        icon: 'user-circle-2',
                        href: '/connect/demographics',
                        seedPrompt: 'Build an audience: parents, QSR weekly, top 20 DMAs',
                        color: '#2E6649',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Attitudes',
                        icon: 'brain',
                        href: '/connect/attitudes',
                        seedPrompt: 'Profile my target’s attitudes and values against the category',
                        color: '#2E6649',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Monthly Opinions',
                        icon: 'calendar',
                        href: '/connect/monthly-opinions',
                        seedPrompt: 'What are consumers saying about my category this month?',
                        color: '#2E6649',
                        hasIconBorder: true,
                    },
                ],
            },
            {
                name: 'Behavior',
                icon: 'footprints',
                color: '#418363',
                hasIconBorder: true,
                tiles: [
                    {
                        name: 'Location',
                        icon: 'map-pin',
                        href: '/connect/location',
                        seedPrompt:
                            'Weekly visit trend for my stores vs. the two nearest competitors',
                        color: '#418363',
                        hasIconBorder: true,
                    },
                    {
                        name: 'App',
                        icon: 'smartphone',
                        href: '/connect/app',
                        seedPrompt:
                            'Compare daily active minutes for my app vs. my top 3 competitors',
                        color: '#418363',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Web',
                        icon: 'globe',
                        href: '/connect/web',
                        seedPrompt: 'Trace the path from search to my site to a store visit',
                        color: '#418363',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Purchase',
                        icon: 'receipt',
                        href: '/connect/purchase',
                        seedPrompt:
                            'What ends up in the basket alongside my brand, and at what price?',
                        color: '#418363',
                        hasIconBorder: true,
                    },
                    {
                        name: 'ChatGPT',
                        icon: 'sparkles',
                        href: '/connect/gen-ai-chatgpt',
                        seedPrompt: 'What do shoppers ask ChatGPT about my category?',
                        color: '#418363',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Gemini',
                        icon: 'sparkles',
                        href: '/connect/gen-ai-gemini',
                        seedPrompt: 'What do shoppers ask Gemini about my category?',
                        color: '#418363',
                        hasIconBorder: true,
                        // Held back for stakeholder review — same quietly
                        // inert treatment as Slide Creator, not a real
                        // entitlement gate.
                        isTemporarilyInactive: true,
                        hideComingSoonLabel: true,
                    },
                ],
            },
            {
                name: 'Exposure',
                icon: 'radio-tower',
                color: '#639D84',
                hasIconBorder: true,
                tiles: [
                    {
                        name: 'Digital Ads',
                        icon: 'megaphone',
                        href: '/connect/digital-ads',
                        seedPrompt:
                            'Who was exposed to my social campaign, by platform and duration?',
                        color: '#639D84',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Media',
                        icon: 'play-circle',
                        href: '/connect/media',
                        seedPrompt:
                            'Where does my audience actually watch: YouTube, Netflix, or Prime?',
                        color: '#639D84',
                        hasIconBorder: true,
                    },
                    {
                        name: 'Audio',
                        icon: 'headphones',
                        href: '/connect/audio',
                        seedPrompt:
                            'How does my audience split listening across music, podcasts, and radio?',
                        color: '#639D84',
                        hasIconBorder: true,
                    },
                ],
            },
        ],
    },
    {
        key: 'report',
        stage: 'Report',
        icon: 'presentation',
        color: STAGE_COLORS.report,
        tagline: 'Build reports & share findings.',
        tiles: [
            {
                name: 'Dashboards',
                icon: 'layout-dashboard',
                color: '#E65E19',
                hasIconBorder: true,
                // Clicking drills into these as icon tiles (Connect's
                // category pattern) rather than a hover dropdown.
                opensActionsPanel: true,
                // The three dashboard hubs (`@dashboards/hubs.ts`), then each
                // client's own vTracker (org-matched; internal viewers see all
                // five). Shared with the nav's Dashboards flyout.
                actions: [...DASHBOARD_HUB_LINKS, ...VTRACKER_DASHBOARDS],
            },
            {
                name: 'Slide Creator',
                icon: 'presentation',
                href: Routes.SLIDE_CREATION,
                // Intentionally NOT `SLIDE_CREATOR_COLOR` here — this tile's
                // icon now follows the Report stage's orange family, while
                // the shared constant keeps driving the Slide Creation page
                // header and its nav icon, which weren't part of this
                // Report-tile color request.
                color: '#EA773E',
                hasIconBorder: true,
                internalTooltip: 'Roadmap v2.1 (Internal Only)',
                // Withheld by a flag is finished work this person cannot see, not work
                // nobody has done — so it must never caption itself "Coming soon".
                hideComingSoonLabel: true,
            },
            {
                name: 'Board Reports',
                icon: 'file-bar-chart',
                href: Routes.REPORTS,
                color: '#EE9163',
                hasIconBorder: true,
            },
        ],
    },
];
